import { useContext, useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { DataGridContext, DataGridContextType } from './context';
import DataGridUI from './DataGridUI';
import { CustomToolbar } from './components/CustomToolbar';
import CustomFilterPanel from './components/CustomFilterPanel';
import { LinearProgress, Box } from '@mui/material';
import { GridFilterModel, GridSortItem, GridSortModel } from '@mui/x-data-grid';
import { useQueryClient } from '@tanstack/react-query';
import { fetchAllDataFromAPI } from './api/dataGridApiService';

type DataGridSateManagementPropsType = {
  apiEndpoint: string;
  params: any;
  pageMode: string;
  pageSize: number;
  rendermode: string;
  filterable: boolean;
  editable: boolean;
  exportable: boolean;
  onRowClick: (row: any) => void;
  onMultiSelect: (selectedRows: any[]) => void;
  gridHeight: string;
  exportFileName: string;
  exportColumns: string;
  checkboxSelection: boolean;
  columnResizable: boolean;
  isRowSelectable: boolean;
  isOpportunity: boolean;
  multiSelectable: boolean;
  renderCellMapping: Array<any>;
  key?: string | number;
};
const DataGridStateManagement = ({
  apiEndpoint,
  params,
  pageMode,
  pageSize,
  rendermode,
  gridHeight,
  filterable,
  exportable,
  editable,
  exportFileName,
  exportColumns,
  onRowClick,
  onMultiSelect,
  columnResizable,
  isRowSelectable,
  multiSelectable,
  isOpportunity,
  renderCellMapping,
  key,
}: DataGridSateManagementPropsType) => {
  const {
    data,
    setData,
    headers,
    setHeaders,
    currentPage,
    setCurrentPage,
    totalDocuments,
    setTotalDocuments,
    totalPages,
    loading,
    setLoading,
    columnSortModel = [],
    setColumnSortModel,
    columnVisibilityModel,
    columnFilterModel,
    setColumnFilterModel,
    setColumnVisibilityModel,
    rowSelectionModel = [],
    setRowSelectionModel,
    fetchData,
    forceUpdate,
  } = useContext<DataGridContextType>(DataGridContext);

  const queryClient = useQueryClient();
  const location = useLocation();
  const isInAIAvatarLayout = location.pathname.startsWith('/ai-avatar-layout');
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [clickedColumnField, setClickedColumnField] = useState<string | null>(null);
  const gridContainerRef = useRef<HTMLDivElement>(null);
  
  // Draggable filter panel state
  const [filterPanelPosition, setFilterPanelPosition] = useState({ x: 20, y: 60 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isDragged, setIsDragged] = useState(false); // Track if panel has been dragged
  
  // Resizable filter panel state
  const [filterPanelSize, setFilterPanelSize] = useState({ width: 450, height: 250 });
  const [isResizing, setIsResizing] = useState(false);
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, width: 0, height: 0 });

  const handleFilterClick = () => {
    setShowFilterPanel(!showFilterPanel);
  };

  // Drag handlers for filter panel
  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.target instanceof HTMLElement) {
      // Check if clicking on the header (filter panel title area)
      const headerElement = e.target.closest('[id="filter-panel-title"]');
      if (headerElement) {
        setIsDragging(true);
        const rect = headerElement.getBoundingClientRect();
        setDragOffset({
          x: e.clientX - rect.left,
          y: e.clientY - rect.top
        });
        e.preventDefault();
      }
    }
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (isDragging) {
      const newX = e.clientX - dragOffset.x;
      const newY = e.clientY - dragOffset.y;
      
      // Keep panel within viewport bounds
      const maxX = window.innerWidth; // panel width
      const maxY = window.innerHeight; // panel height
      
      setFilterPanelPosition({
        x: Math.max(0, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY))
      });
      setIsDragged(true); // Mark as dragged
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Add global mouse event listeners for dragging
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, dragOffset]);

  // Resize handlers for filter panel
  const handleResizeStart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    setResizeStart({
      x: e.clientX,
      y: e.clientY,
      width: filterPanelSize.width,
      height: filterPanelSize.height
    });
  };

  const handleResizeMove = (e: MouseEvent) => {
    if (isResizing) {
      const deltaX = e.clientX - resizeStart.x;
      const deltaY = e.clientY - resizeStart.y;
      
      const newWidth = Math.max(350, Math.min(800, resizeStart.width + deltaX));
      const newHeight = Math.max(250, Math.min(1200, resizeStart.height + deltaY));
      
      setFilterPanelSize({
        width: newWidth,
        height: newHeight
      });
    }
  };

  const handleResizeEnd = () => {
    setIsResizing(false);
  };

  // Handle panel size change from filter panel
  const handlePanelSizeChange = (newSize: { width: number; height: number }) => {
    setFilterPanelSize(newSize);
  };

  // Add global mouse event listeners for resizing
  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleResizeMove);
      document.addEventListener('mouseup', handleResizeEnd);
      
      return () => {
        document.removeEventListener('mousemove', handleResizeMove);
        document.removeEventListener('mouseup', handleResizeEnd);
      };
    }
  }, [isResizing, resizeStart]);

  // Handle column menu click to open filter panel with selected column
  const handleColumnMenuClick = (columnField: string) => {
    setClickedColumnField(columnField);
    setShowFilterPanel(true);
  };

  // Auto-focus DataGrid on hover
  const handleMouseEnter = () => {
    // Small delay to prevent accidental focus loss
    setTimeout(() => {
      if (gridContainerRef.current) {
        gridContainerRef.current.focus();
      }
    }, 100);
  };

  // Optional: Remove focus when mouse leaves (uncomment if needed)
  // const handleMouseLeave = () => {
  //   if (gridContainerRef.current) {
  //     gridContainerRef.current.blur();
  //   }
  // };

  // Close filter panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      
      // Don't close if clicking on date picker elements
      if (target.closest('.MuiPickersPopper-root') || 
          target.closest('.MuiDateCalendar-root') ||
          target.closest('.MuiPickersDay-root') ||
          target.closest('.MuiPickersCalendarHeader-root')) {
        return;
      }
      
      if (showFilterPanel && !target.closest('.filter-panel') && !target.closest('.filter-button')) {
        setShowFilterPanel(false);
        setClickedColumnField(null); // Reset clicked column when closing
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showFilterPanel]);

  // Add event listeners for column menu clicks
  useEffect(() => {
    const handleColumnMenuClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      
      // Check if it's a filter menu item
      const isFilterMenuItem = target.textContent?.toLowerCase().includes('filter') || 
                              target.closest('[data-value="filter"]') ||
                              target.closest('.MuiMenuItem-root')?.textContent?.toLowerCase().includes('filter');
      
      if (isFilterMenuItem && filterable) {
        // Find the column header that contains this menu
        const menuPopper = target.closest('.MuiPopper-root') || 
                          target.closest('.MuiMenu-root') ||
                          target.closest('.MuiPopover-root');
        
        if (menuPopper) {
          // Try to find the column header that triggered this menu
          // First check if this menu is related to our grid by looking for the grid's unique identifier
          const gridContainer = gridContainerRef.current;
          if (!gridContainer) return;
          
          // Look for column headers within our grid that have an active menu
          const columnHeaders = gridContainer.querySelectorAll('.MuiDataGrid-columnHeader');
          let targetColumnField: string | null = null;
          
          // Look for the column header that has an active menu
          columnHeaders.forEach((header) => {
            const menuButton = header.querySelector('.MuiDataGrid-menuIconButton');
            if (menuButton && menuButton.getAttribute('aria-expanded') === 'true') {
              targetColumnField = header.getAttribute('data-field');
            }
          });
          
          if (targetColumnField) {
            // Don't prevent default - let the menu close naturally
            setClickedColumnField(targetColumnField);
            setShowFilterPanel(true);
          }
        }
      }
    };

    // Also try mousedown event as a backup
    const handleColumnMenuMouseDown = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      
      const isFilterMenuItem = target.textContent?.toLowerCase().includes('filter') || 
                              target.closest('[data-value="filter"]') ||
                              target.closest('.MuiMenuItem-root')?.textContent?.toLowerCase().includes('filter');
      
      if (isFilterMenuItem && filterable) {
        const menuPopper = target.closest('.MuiPopper-root') || 
                          target.closest('.MuiMenu-root') ||
                          target.closest('.MuiPopover-root');
        
        if (menuPopper) {
          const gridContainer = gridContainerRef.current;
          if (!gridContainer) return;
          
          const columnHeaders = gridContainer.querySelectorAll('.MuiDataGrid-columnHeader');
          let targetColumnField: string | null = null;
          
          columnHeaders.forEach((header) => {
            const menuButton = header.querySelector('.MuiDataGrid-menuIconButton');
            if (menuButton && menuButton.getAttribute('aria-expanded') === 'true') {
              targetColumnField = header.getAttribute('data-field');
            }
          });
          
          if (targetColumnField) {
            // Don't prevent default - let the menu close naturally
            setClickedColumnField(targetColumnField);
            setShowFilterPanel(true);
          }
        }
      }
    };

    // Use MutationObserver to detect when menu items are added
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const element = node as Element;
              const filterMenuItems = element.querySelectorAll('.MuiMenuItem-root, [data-value="filter"]');
              filterMenuItems.forEach((item) => {
                const textContent = item.textContent?.toLowerCase();
                if (textContent && textContent.includes('filter')) {
                  item.addEventListener('click', (e) => {
                    const menuPopper = item.closest('.MuiPopper-root') || 
                                      item.closest('.MuiMenu-root') ||
                                      item.closest('.MuiPopover-root');
                    
                    if (menuPopper) {
                      const gridContainer = gridContainerRef.current;
                      if (!gridContainer) return;
                      
                      const columnHeaders = gridContainer.querySelectorAll('.MuiDataGrid-columnHeader');
                      let targetColumnField: string | null = null;
                      
                      columnHeaders.forEach((header) => {
                        const menuButton = header.querySelector('.MuiDataGrid-menuIconButton');
                        if (menuButton && menuButton.getAttribute('aria-expanded') === 'true') {
                          targetColumnField = header.getAttribute('data-field');
                        }
                      });
                      
                      if (targetColumnField && filterable) {
                        // Don't prevent default - let the menu close naturally
                        setClickedColumnField(targetColumnField);
                        setShowFilterPanel(true);
                      }
                    }
                  });
                }
              });
            }
          });
        }
      });
    });

    // Start observing document body for menu items
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    document.addEventListener('click', handleColumnMenuClick);
    document.addEventListener('mousedown', handleColumnMenuMouseDown);
    
    return () => {
      document.removeEventListener('click', handleColumnMenuClick);
      document.removeEventListener('mousedown', handleColumnMenuMouseDown);
      observer.disconnect();
    };
  }, [filterable]);

  const handleFetchData = (page: number) => {
    if (
      page > totalPages ||
      loading ||
      (data.length >= totalDocuments && totalDocuments > 0)
    ) {
      return;
    }
    fetchData(
      apiEndpoint,
      params,
      page,
      pageSize,
      columnFilterModel,
      false,
      columnSortModel,
    );
  };

  const handleFilterApply = (columnFilterModel: GridFilterModel) => {
    console.log('Applying filter:', columnFilterModel);
    
    // For client-side filtering, don't make API calls
    if (rendermode === 'client') {
      console.log('Client-side filtering - no API call needed');
      // Just update the filter model, DataGrid will handle filtering internally
      return;
    }
    
    // For server-side filtering, make API call
    setTotalDocuments(0);
    setData([]); // Clear existing data when applying new filter
    fetchData(
      apiEndpoint,
      params,
      1,
      pageSize,
      columnFilterModel,
      true,
      columnSortModel,
    );
  };

  // Handle filter model changes from DataGrid
  const handleFilterModelChange = (model: GridFilterModel) => {
    console.log('Filter model changed:', model);
    setColumnFilterModel(model);
    
    // For client-side filtering, DataGrid handles filtering internally
    // No need to call handleFilterApply as it would make API calls
    // The filter model is already updated and DataGrid will filter automatically
  };

  useEffect(() => {
    fetchData(apiEndpoint, params, 1, pageSize);
  }, []);

  // Add effect to watch for key changes and refresh data
  useEffect(() => {
    if (key !== undefined) {
      // Remove all queries that start with ['GET', apiEndpoint] to clear cache
      queryClient.removeQueries({ 
        queryKey: ['GET', apiEndpoint],
        exact: false 
      });
      
      // Reset data state
      setData([]);
      setTotalDocuments(0);
      
      // Fetch fresh data
      fetchData(apiEndpoint, params, 1, pageSize);
    }
  }, [key]);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    // Global keyboard shortcuts for the entire grid
    if (event.key === 'f' || event.key === 'F') {
      event.preventDefault();
      if (filterable) {
        setShowFilterPanel(!showFilterPanel);
      }
    } else if (event.key === 'e' || event.key === 'E') {
      event.preventDefault();
      if (exportable) {
        handleFullExport();
      }
    } else if (event.key === 'Enter') {
      handleFilterApply(columnFilterModel);
    } else if (event.key === 'Escape') {
      // Close filter panel if open
      if (showFilterPanel) {
        setShowFilterPanel(false);
      } else {
        // Reset filters
        setColumnFilterModel({
          items: [{ columnField: 'name', operatorValue: 'contains', value: '' }],
        });
        if (rendermode === 'server') {
          fetchData(apiEndpoint, params, 1, pageSize, {
            items: [
              { columnField: 'name', operatorValue: 'contains', value: '' },
            ],
          });
        }
      }
    }
  };

  const handleSortModelChange = (model: GridSortModel) => {
    setColumnSortModel(model);
    if (rendermode === 'server') {
      setTotalDocuments(0);
      fetchData(
        apiEndpoint,
        params,
        1,
        pageSize,
        columnFilterModel,
        true,
        model,
      );
    }
  };

  // Export function for keyboard shortcut
  const handleFullExport = async () => {
    try {
      const response = await fetchAllDataFromAPI(apiEndpoint, params);
      if (!response?.body?.data || !Array.isArray(response.body.data)) {
        return;
      }
      
      // Convert to CSV
      const headers = Object.keys(response.body.data[0]).join(',');
      const rows = response.body.data.map(obj => 
        Object.values(obj).map(value => 
          `"${String(value).replace(/"/g, '""')}"`
        ).join(',')
      );
      const csvContent = [headers, ...rows].join('\n');
      
      // Download CSV
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `${exportFileName || 'export'}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  // Get OS-specific keyboard shortcut text
  const getKeyboardShortcut = (key: string) => {
    const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
    return isMac ? `⌘${key}` : `Ctrl+${key}`;
  };

  return (
    <div style={{ position: 'relative' }}>
      {showFilterPanel && filterable && (
        <Box
          sx={{
            position: isDragged ? 'fixed' : 'absolute',
            top: isDragged ? filterPanelPosition.y : 60,
            left: isDragged ? filterPanelPosition.x : 20,
            zIndex: 1000,
          }}
        >
          <CustomFilterPanel
            handleFilterApply={handleFilterApply}
            rendermode={rendermode}
            onClose={() => setShowFilterPanel(false)}
            columnField={clickedColumnField}
            onMouseDown={handleMouseDown}
            isDragging={isDragging}
            panelSize={filterPanelSize}
            onResizeStart={handleResizeStart}
            isResizing={isResizing}
            onPanelSizeChange={handlePanelSizeChange}
          />
        </Box>
      )}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          maxHeight: `${gridHeight}px`,
          minHeight: `${gridHeight}px`,
        }}
        onKeyDown={handleKeyDown}
        tabIndex={0} // Make the div focusable
        ref={gridContainerRef}
        onMouseEnter={handleMouseEnter}
        // onMouseLeave={handleMouseLeave}
      >
        <DataGridUI
          key={`grid-${forceUpdate}-${JSON.stringify(columnFilterModel)}`}
          isInAIAvatarLayout={isInAIAvatarLayout}
          columnBuffer={headers.length}
          columnThreshold={10}
          // rowBuffer={100}
          disableVirtualization
          rows={data}
          columns={headers}
          sortModel={columnSortModel}
          onSortModelChange={handleSortModelChange}
          pagination={pageMode === 'pagination'}
          paginationMode={rendermode === 'server' ? 'server' : 'client'}
          page={currentPage}
          rowCount={totalDocuments}
          onPageChange={handleFetchData}
          pageSize={pageSize}
          rowsPerPageOptions={[10, 20, 50, 100]}
          disableSelectionOnClick
          getRowId={row => row?._id}
          filterMode={rendermode === 'server' ? 'server' : 'client'}
          sortingMode={rendermode === 'server' ? 'server' : 'client'}
          filterModel={columnFilterModel}
          onFilterModelChange={handleFilterModelChange}
          components={{
            Toolbar: CustomToolbar,
            FilterPanel: CustomFilterPanel,
            LoadingOverlay: LinearProgress,
          }}
          componentsProps={{
            filterPanel: {
              columnsSort: 'asc',
              handleFilterApply,
              rendermode,
              // Prevent filter panel from closing
              slotProps: {
                filterForm: {
                  onMouseDown: (e) => e.stopPropagation(),
                  onClick: (e) => e.stopPropagation(),
                },
              },
            },
            toolbar: {
              filterable,
              exportable,
              exportFileName,
              exportColumns,
              editable,
              apiEndpoint,
              params,
              onFilterClick: handleFilterClick,
              showKeyboardShortcuts: true,
            },
          }}

          checkboxSelection={multiSelectable}
          onSelectionModelChange={newSelectionModel => {
            setRowSelectionModel(newSelectionModel);
            onMultiSelect(newSelectionModel);
          }}
          selectionModel={rowSelectionModel}
          isRowSelectable={() => isRowSelectable}
          onRowClick={
            onRowClick
            // && !isEditEnabled ? onRowClick : undefined
          }
          // getRowClassName={getRowClassName}
          columnVisibilityModel={
            columnVisibilityModel ? columnVisibilityModel : {}
          }
          // style={{ gridAutoColumns: 'minmax(100px, auto)' }}
          onColumnVisibilityModelChange={
            setColumnVisibilityModel
              ? newModel => setColumnVisibilityModel(newModel)
              : () => null
          }
          loading={loading} // Show loading indicator for infinite scroll
          // density="standard"
          disableColumnResize={!columnResizable}
          // experimentalFeatures={{
          //   newEditingApi: true,
          // }}
          // onCellClick={(params, event) => {
          //   onCellClick(params)
          //   // console.log("Cell clicked:", params)
          // }}
          onCellDoubleClick={(params, event) =>
            console.log('Cell double-clicked:', params)
          }
          onColumnOrderChange={newOrder =>
            console.log('Column order changed:', newOrder)
          }
          onProcessRowUpdateError={error =>
            console.error('Row update error:', error)
          }
          sortingOrder={['asc', 'desc']}
          onRowsScrollEnd={() => handleFetchData(currentPage + 1)} // Infinite scroll event
          // classes={{ columnHeader: classes.headerCell }}
        />
      </div>
    </div>
  );
};

export default DataGridStateManagement;
